// src/utils/bankStatementParser.js
// Universal Indian Bank Statement Parser
// Supports: IDFC First, Slice/OneCard, HDFC, SBI, ICICI, Axis, Kotak, Yes Bank,
//           IndusInd, Federal, PNB, BOB, Canara, Union, and generic formats.

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

// ── Category Rules ──────────────────────────────────────────────
const CATEGORY_RULES = [
  { keywords: ['amazon', 'flipkart', 'myntra', 'ajio', 'meesho', 'nykaa', 'amznlpa', 'noon.com', 'noon minutes', 'snapdeal', 'tatacliq', 'croma', 'reliance digital', 'shoppers stop', 'lifestyle', 'westside', 'hm ', 'h&m', 'zara'], category: 'Shopping', color: 'blue', icon: 'cart' },
  { keywords: ['zomato', 'swiggy', 'blinkit', 'zepto', 'bigbasket', 'dunzo', 'instamart', 'grofers', 'keeta', 'dmart', 'more supermarket', 'spencers', 'nature basket', 'fresh to home', 'licious', 'country delight'], category: 'Food & Grocery', color: 'orange', icon: 'food' },
  { keywords: ['shell', 'hp petrol', 'indian oil', 'bharat petroleum', 'petrol', 'diesel', 'fuel', 'iocl', 'bpcl', 'hpcl'], category: 'Fuel', color: 'amber', icon: 'fuel' },
  { keywords: ['netflix', 'hotstar', 'spotify', 'youtube premium', 'prime video', 'jio cinema', 'apple music', 'zee5', 'sonyliv', 'audible', 'kindle', 'chatgpt', 'openai', 'claude', 'notion', 'figma', 'canva'], category: 'Subscriptions', color: 'purple', icon: 'tv' },
  { keywords: ['uber', 'ola', 'rapido', 'metro', 'irctc', 'redbus', 'makemytrip', 'goibibo', 'cleartrip', 'yatra', 'baggagetaxi', 'indigo', 'spicejet', 'air india', 'vistara', 'akasa'], category: 'Transport', color: 'cyan', icon: 'car' },
  { keywords: ['zerodha', 'groww', 'trading', 'kite', 'upstox', 'angel', 'motilal', 'iifl', 'sharekhan', 'kotak securities', '5paisa', 'dhan'], category: 'Trading', color: 'emerald', icon: 'chart' },
  { keywords: ['rent', 'society', 'maintenance', 'housing', 'apartment', 'flat', 'nobroker'], category: 'Rent & Housing', color: 'indigo', icon: 'home' },
  { keywords: ['salary', 'credit interest', 'cashback', 'refund', 'interest credit', 'savings interest', 'dividend', 'reversed', 'reversal'], category: 'Income', color: 'green', icon: 'money' },
  { keywords: ['emi', 'loan', 'repayment', 'bajaj fin', 'hdfc ltd', 'mpokket', 'lazypay', 'paysense', 'moneyvie', 'simpl', 'kissht', 'zestmoney', 'fibe', 'cashe', 'kreditbee', 'navi loan', 'apnaco'], category: 'EMI & Loans', color: 'red', icon: 'credit' },
  { keywords: ['electricity', 'water', 'gas', 'broadband', 'airtel', 'jio prepaid', 'jio postpaid', 'vi ', 'recharge', 'dth', 'billpay', 'nhai', 'fastag', 'tata power', 'adani', 'bescom', 'msedcl', 'torrent power', 'act fibernet', 'hathway'], category: 'Utilities', color: 'teal', icon: 'wifi' },
  { keywords: ['hospital', 'pharmacy', 'medical', 'doctor', 'lab', 'diagnostic', 'apollo', 'medplus', 'pharmeasy', 'netmeds', '1mg', 'practo', 'max hospital', 'fortis'], category: 'Healthcare', color: 'rose', icon: 'health' },
  { keywords: ['bookmysh', 'bigtree', 'bookmys', 'pvr', 'inox', 'movie', 'concert', 'event', 'insider'], category: 'Entertainment', color: 'pink', icon: 'entertainment' },
  { keywords: ['paytm', 'phonepe', 'one97', 'google pay', 'gpay'], category: 'Wallet/UPI', color: 'violet', icon: 'wallet' },
  { keywords: ['school', 'college', 'university', 'tuition', 'coaching', 'udemy', 'coursera', 'unacademy', 'byjus'], category: 'Education', color: 'sky', icon: 'education' },
  { keywords: ['insurance', 'lic', 'star health', 'icici lombard', 'hdfc ergo', 'digit', 'acko', 'policy'], category: 'Insurance', color: 'lime', icon: 'shield' },
];

function categorize(description) {
  const lower = description.toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.keywords.some(kw => lower.includes(kw))) {
      return { category: rule.category, color: rule.color };
    }
  }
  if (lower.includes('upi') || lower.includes('/pay')) return { category: 'UPI Transfer', color: 'slate' };
  if (lower.includes('imps') || lower.includes('neft') || lower.includes('rtgs') || lower.includes('ift/')) return { category: 'Fund Transfer', color: 'slate' };
  if (lower.includes('atm') || lower.includes('cash deposit') || lower.includes('cash withdrawal')) return { category: 'Cash', color: 'gray' };
  if (lower.includes('pos') || lower.includes('visa') || lower.includes('mastercard') || lower.includes('rupay')) return { category: 'Card Payment', color: 'gray' };
  return { category: 'Other', color: 'gray' };
}

// ── PDF Text Extraction (with structured row data) ──────────────
async function extractTextFromPDF(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const allLines = [];
  const allRowItems = [];

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();

    // Group text items by Y-coordinate (same row)
    const rows = {};
    for (const item of content.items) {
      if (!item.str.trim()) continue;
      // Round Y to nearest 2px to group items on same visual line
      const y = Math.round(item.transform[5] / 2) * 2;
      if (!rows[y]) rows[y] = [];
      rows[y].push({ text: item.str, x: item.transform[4] });
    }

    // Sort rows top-to-bottom, items left-to-right, join with spacing
    const sortedYs = Object.keys(rows).map(Number).sort((a, b) => b - a);
    for (const y of sortedYs) {
      const items = rows[y].sort((a, b) => a.x - b.x);
      // Join with smart spacing — add tab-like gaps for wide separations
      let line = '';
      let lastX = 0;
      for (const item of items) {
        const gap = item.x - lastX;
        if (line && gap > 30) {
          line += '   '; // column separator
        } else if (line && gap > 5) {
          line += ' ';
        }
        line += item.text;
        lastX = item.x + (item.text.length * 4); // approximate
      }
      if (line.trim()) {
        allLines.push(line.trim());
        allRowItems.push(items);
      }
    }
  }

  return { lines: allLines, rowItems: allRowItems };
}

// ── Date Parsing ────────────────────────────────────────────────
const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  january: 0, february: 1, march: 2, april: 3, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

// All date patterns used by Indian banks
const DATE_REGEXES = [
  // DD-Mon-YYYY (IDFC: 31-Dec-2025)
  { re: /\b(\d{1,2})[-/]([A-Za-z]{3,9})[-/](\d{4})\b/, parse: (m) => monthDate(m[1], m[2], m[3]) },
  // DD-Mon-YY (31-Dec-25)
  { re: /\b(\d{1,2})[-/]([A-Za-z]{3,9})[-/](\d{2})\b/, parse: (m) => monthDate(m[1], m[2], '20' + m[3]) },
  // DD Mon YYYY (31 Dec 2025)
  { re: /\b(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})\b/, parse: (m) => monthDate(m[1], m[2], m[3]) },
  // DD Mon YY (31 Dec 25)
  { re: /\b(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{2})\b/, parse: (m) => monthDate(m[1], m[2], '20' + m[3]) },
  // Mon DD, YYYY (Dec 31, 2025) — some international formats
  { re: /\b([A-Za-z]{3,9})\s+(\d{1,2}),?\s+(\d{4})\b/, parse: (m) => monthDate(m[2], m[1], m[3]) },
  // DD/MM/YYYY or DD-MM-YYYY (most Indian banks)
  { re: /\b(\d{2})[/\-](\d{2})[/\-](\d{4})\b/, parse: (m) => numDate(m[1], m[2], m[3]) },
  // DD/MM/YY
  { re: /\b(\d{2})[/\-](\d{2})[/\-](\d{2})\b/, parse: (m) => numDate(m[1], m[2], '20' + m[3]) },
  // YYYY-MM-DD (ISO)
  { re: /\b(\d{4})-(\d{2})-(\d{2})\b/, parse: (m) => numDate(m[3], m[2], m[1]) },
];

function monthDate(day, monthStr, year) {
  const mi = MONTHS[monthStr.toLowerCase().substring(0, 3)];
  if (mi === undefined) return null;
  const d = parseInt(day), y = parseInt(year);
  if (d < 1 || d > 31 || y < 2015 || y > 2030) return null;
  return new Date(y, mi, d);
}

function numDate(day, month, year) {
  const d = parseInt(day), m = parseInt(month), y = parseInt(year);
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 2015 || y > 2030) return null;
  return new Date(y, m - 1, d);
}

function findDate(text) {
  for (const { re, parse } of DATE_REGEXES) {
    const match = text.match(re);
    if (match) {
      const date = parse(match);
      if (date && !isNaN(date.getTime())) {
        return { date, matched: match[0], index: match.index, endIndex: match.index + match[0].length };
      }
    }
  }
  return null;
}

// ── Amount Parsing ──────────────────────────────────────────────
// Indian format: 1,23,456.78 or 1,234.56 or 123.45 or 12,345
const AMOUNT_RE = /(?:(?:Rs\.?|INR|₹)\s*)?(\d{1,3}(?:,\d{2,3})*(?:\.\d{1,2})?|\d+\.\d{1,2})\b/g;

function parseAmountVal(raw) {
  const num = parseFloat(raw.replace(/[₹,\s]/g, '').replace(/^(Rs\.?|INR)\s*/i, ''));
  return isNaN(num) ? 0 : Math.abs(num);
}

function findAmounts(text) {
  const results = [];
  const re = new RegExp(AMOUNT_RE.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    const val = parseAmountVal(m[0]);
    // Must be a meaningful amount (not a year, not a tiny ref number)
    const raw = m[1];
    if (/^\d{4}$/.test(raw) && parseInt(raw) >= 2015 && parseInt(raw) <= 2030) continue;
    if (/^\d{6,}$/.test(raw)) continue;
    if (val >= 0.01) {
      results.push({ value: val, index: m.index, endIndex: m.index + m[0].length, raw: m[0] });
    }
  }
  return results;
}

// ── Credit/Debit Detection (keyword-based fallback) ─────────────
function detectTxnType(text) {
  const t = text.toLowerCase();

  // Strong credit signals
  if (/\bupi\/cr\b/i.test(text)) return 'credit';
  if (/\bneft\s*cr\b/i.test(text)) return 'credit';
  if (/\bimps\/\d+\/[A-Z]/i.test(text) && !/imps-opm/i.test(text)) return 'credit';
  if (/\bift\//i.test(text) && !/upi\/ift/i.test(text)) return 'credit';
  if (t.includes('cash deposit') || t.includes('atm/cash deposit')) return 'credit';
  if (t.includes('interest credit') || t.includes('savings interest')) return 'credit';
  if (t.includes('-cancelled') || t.includes('reversed') || t.includes('reversal')) return 'credit';
  if (/\bref\//.test(t) && (t.includes('pos') || t.includes('visa'))) return 'credit';
  if (/\bcredited\b/.test(t)) return 'credit';
  if (/\bby\s+transfer\b/.test(t)) return 'credit';
  if (t.includes('salary')) return 'credit';
  if (t.includes('refund') && !t.includes('no refund')) return 'credit';

  // Strong debit signals
  if (/\bupi\/dr\b/i.test(text)) return 'debit';
  if (/\bupi\/ift\b/i.test(text)) return 'debit';
  if (/\bpos[-/]visa\b/i.test(text) && !t.includes('cancelled')) return 'debit';
  if (/\bimps-opm\b/i.test(text)) return 'debit';
  if (t.includes('billpay/') || t.includes('bill payment')) return 'debit';
  if (/\bneft\s*dr\b/i.test(text)) return 'debit';
  if (/\bdebited\b/.test(t)) return 'debit';
  if (t.includes('purchase') || t.includes('payment')) return 'debit';
  if (t.includes('atm withdrawal') || t.includes('atm/cash withdrawal')) return 'debit';

  return null; // unknown — caller decides
}

// ── Column Layout Detection ─────────────────────────────────────
// Scans header rows for Debit/Credit/Balance column positions
function detectColumnLayout(lines, rowItems) {
  const DEBIT_KW = ['debit', 'withdrawal', 'dr amount', 'dr.'];
  const CREDIT_KW = ['credit', 'deposit', 'cr amount', 'cr.'];
  const BALANCE_KW = ['balance', 'closing bal', 'running bal'];

  for (let i = 0; i < Math.min(40, lines.length); i++) {
    const lower = lines[i].toLowerCase();
    const hasDebit = DEBIT_KW.some(kw => lower.includes(kw));
    const hasCredit = CREDIT_KW.some(kw => lower.includes(kw));

    if (hasDebit && hasCredit && rowItems[i]) {
      const items = rowItems[i];
      let debitX = null, creditX = null, balanceX = null;

      for (const item of items) {
        const t = item.text.toLowerCase().trim();
        if (debitX === null && DEBIT_KW.some(kw => t.includes(kw))) {
          debitX = item.x;
        }
        if (creditX === null && CREDIT_KW.some(kw => t.includes(kw))) {
          creditX = item.x;
        }
        if (balanceX === null && BALANCE_KW.some(kw => t.includes(kw))) {
          balanceX = item.x;
        }
      }

      if (debitX !== null && creditX !== null) {
        const gap = Math.abs(creditX - debitX);
        const tolerance = Math.max(gap * 0.4, 20);
        return { debitX, creditX, balanceX, tolerance, headerRow: i };
      }
    }
  }
  return null;
}

// ── Column-based Amount Classification ──────────────────────────
// Checks row items X-positions against known column layout
function classifyByColumn(lineIndices, rowItems, columnLayout) {
  if (!columnLayout) return null;

  let debitAmt = null, creditAmt = null, balanceAmt = null;
  const { debitX, creditX, balanceX, tolerance } = columnLayout;

  for (const idx of lineIndices) {
    if (!rowItems[idx]) continue;
    for (const item of rowItems[idx]) {
      // Try to parse as currency amount
      const cleaned = item.text.replace(/[₹,\s]/g, '').replace(/^(Rs\.?|INR)\s*/i, '');
      const val = parseFloat(cleaned);
      if (isNaN(val) || val < 0.01) continue;
      // Skip values that look like years
      if (/^\d{4}$/.test(cleaned) && parseInt(cleaned) >= 2015 && parseInt(cleaned) <= 2030) continue;
      // Skip long reference numbers
      if (/^\d{6,}$/.test(cleaned)) continue;

      // Match item's X-position to the closest column
      const dDist = Math.abs(item.x - debitX);
      const cDist = Math.abs(item.x - creditX);
      const bDist = balanceX !== null ? Math.abs(item.x - balanceX) : Infinity;

      if (dDist <= tolerance && dDist <= cDist && dDist <= bDist) {
        debitAmt = val;
      } else if (cDist <= tolerance && cDist <= dDist && cDist <= bDist) {
        creditAmt = val;
      } else if (bDist <= tolerance) {
        balanceAmt = val;
      }
    }
  }

  if (debitAmt !== null && creditAmt === null) {
    return { type: 'debit', amount: debitAmt, balance: balanceAmt };
  }
  if (creditAmt !== null && debitAmt === null) {
    return { type: 'credit', amount: creditAmt, balance: balanceAmt };
  }
  if (debitAmt !== null && creditAmt !== null) {
    // Both columns have values — rare; prefer debit
    return { type: 'debit', amount: debitAmt, balance: balanceAmt };
  }

  return null;
}

// ── Lines to Skip ───────────────────────────────────────────────
const SKIP_PATTERNS = [
  /^STATEMENT\s+OF\s+ACCOUNT/i,
  /^CUSTOMER\s+(ID|NAME)/i,
  /^ACCOUNT\s+(NO|BRANCH|STATUS|TYPE|OPENING)/i,
  /^STATEMENT\s+PERIOD/i,
  /^COMMUNICATION/i,
  /^ADDRESS/i,
  /^EMAIL\s+ID/i,
  /^PHONE\s+NO/i,
  /^CKYC/i,
  /^NOMINATION/i,
  /^NOMINEE/i,
  /^CURRENCY/i,
  /^Opening\s+Balance\s+Total/i,
  /^Transaction.*Value\s+Date/i,
  /^Date\s+No/i,
  /^Sr\.?\s*No/i,
  /^S\.?\s*No/i,
  /^Txn\s+Date/i,
  /^Sl\.?\s*No/i,
  /^REGISTERED\s+OFFICE/i,
  /^Page\s+\d+\s+(of|\/)\s+\d+/i,
  /^IMPORTANT/i,
  /^COMMONLY\s+USED/i,
  /^CONTACT\s+US/i,
  /^GRIEVANCE/i,
  /^SAFETY\s+TIPS/i,
  /^---+/,
  /^===+/,
  /^\*\*\*+/,
  /^This\s+is\s+a\s+(system|computer)\s+generated/i,
  /^Disclaimer/i,
  /^Note:/i,
  /^Terms\s+and\s+Conditions/i,
  /^(Your\s+)?(Deposit|debit\s+card)/i,
  /^\s*$/, // empty
  /^(A2A|ATM|CR|CVV|DR|FD|FT|IDFC|IFSC|IFT|IMPS|MICR|NEFT|OTP|PIN|POS|RD|RTGS|SI|TPT|TRF|TXN|UPI|URN)\s{2,}/i, // abbreviation definitions
  /^(Do\s+not|Never|Unless)/i, // safety tips
  /^(Mr\.|Mrs\.|Ms\.)\s+/i, // customer name lines (when standalone)
  /^(VILLA|PLOT|FLAT|HOUSE|FLOOR|GROUND)/i, // address lines
  /^(HYDERABAD|MUMBAI|DELHI|CHENNAI|BANGALORE|KOLKATA|PUNE)/i, // city lines
  /^(TELANGANA|MAHARASHTRA|KARNATAKA|TAMILNADU|KERALA|ANDHRA)/i, // state lines
  /^Opening\s+Balance\s+[\d,.]+$/i,
];

function shouldSkipLine(line) {
  if (line.length < 8) return true;
  if (SKIP_PATTERNS.some(p => p.test(line))) return true;
  // Lines that are ONLY amounts (summary rows like "0.00   5,62,465.93   5,64,288.08   1,822.15")
  if (/^[\d,.\s]+$/.test(line) && line.includes('.')) return true;
  return false;
}

// ── Universal Transaction Parser (column-aware) ─────────────────
function parseTransactions(lines, rowItems, bankName, columnLayout) {
  const transactions = [];
  const processed = new Set();
  let lastBalance = null;

  for (let i = 0; i < lines.length; i++) {
    if (processed.has(i)) continue;
    const line = lines[i];

    if (shouldSkipLine(line)) continue;

    // Skip lines at or before the header row
    if (columnLayout && i <= columnLayout.headerRow) continue;

    // Must have a date
    const dateResult = findDate(line);
    if (!dateResult) continue;

    // Skip if date is unreasonable
    const now = new Date();
    const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
    const nextYear = new Date(now.getFullYear() + 1, 11, 31);
    if (dateResult.date < twoYearsAgo || dateResult.date > nextYear) continue;

    // Gather the transaction text: current line + continuation lines
    let afterDate = line.substring(dateResult.endIndex).trim();

    // Remove second date (value date) if present
    const secondDate = findDate(afterDate);
    if (secondDate && secondDate.index < 20) {
      afterDate = afterDate.substring(secondDate.endIndex).trim();
    }

    // Collect continuation lines and track their indices
    let fullText = afterDate;
    const lineIndices = [i];
    let j = i + 1;
    while (j < lines.length && j <= i + 4) {
      const next = lines[j];
      if (shouldSkipLine(next)) break;
      if (findDate(next)) break; // new transaction
      fullText += ' ' + next.trim();
      lineIndices.push(j);
      processed.add(j);
      j++;
    }

    fullText = fullText.replace(/\s+/g, ' ').trim();
    if (fullText.length < 3) continue;

    // Try column-based classification first
    const colResult = classifyByColumn(lineIndices, rowItems, columnLayout);

    // Find amounts from joined text (still needed for description extraction)
    const amounts = findAmounts(fullText);
    if (amounts.length === 0 && !colResult) continue;

    // Description = text before first amount
    let description = amounts.length > 0
      ? fullText.substring(0, amounts[0].index).trim()
      : fullText.trim();
    description = description.replace(/^[\s\-/|:]+/, '').replace(/[\s\-/|:]+$/, '').trim();

    // If description is empty, use the raw text up to the amount
    if (!description || description.length < 2) {
      description = afterDate.substring(0, afterDate.search(/\d{1,3}(?:,\d{2,3})*\.\d{2}/));
      description = description.replace(/^[\s\-/|:]+/, '').replace(/[\s\-/|:]+$/, '').trim();
    }
    if (!description || description.length < 2) continue;

    // Determine type and amount
    let type, amount;

    if (colResult) {
      // Column-based classification succeeded
      type = colResult.type;
      amount = colResult.amount;
      if (colResult.balance !== null && colResult.balance !== undefined) {
        lastBalance = colResult.balance;
      }
    } else {
      // Fallback to keyword detection with category-aware default
      const txnType = detectTxnType(fullText);
      amount = amounts[0].value;
      // Category-aware fallback: Income category → credit, else → debit
      const { category: cat } = categorize(description);
      type = txnType || (cat === 'Income' ? 'credit' : 'debit');
    }

    if (!amount || amount <= 0) continue;

    // Clean description
    description = description.replace(/\/+$/, '').replace(/\s+/g, ' ').trim();
    if (description.length > 120) description = description.substring(0, 120);

    // Store date at noon local to prevent UTC day-shift (midnight IST = previous day UTC)
    const txnDate = new Date(dateResult.date);
    txnDate.setHours(12, 0, 0, 0);

    const { category, color } = categorize(description);
    transactions.push({
      date: txnDate.toISOString(),
      description,
      amount,
      type,
      category,
      categoryColor: color,
      bank: bankName,
      rawText: line.substring(0, 200),
    });
  }

  return { transactions, lastBalance };
}

// ── Bank Auto-Detection ─────────────────────────────────────────
const BANK_PATTERNS = [
  { keywords: ['idfc first', 'idfc bank', 'idfcfirstb', 'idfb'], name: 'IDFC First' },
  { keywords: ['slice', 'onecard', 'one card'], name: 'Slice' },
  { keywords: ['hdfc bank', 'hdfcbank'], name: 'HDFC' },
  { keywords: ['state bank of india', 'sbi ', 'onlinesbi'], name: 'SBI' },
  { keywords: ['icici bank', 'icicibank'], name: 'ICICI' },
  { keywords: ['axis bank', 'axisbank'], name: 'Axis' },
  { keywords: ['kotak mahindra', 'kotak bank'], name: 'Kotak' },
  { keywords: ['yes bank', 'yesbank'], name: 'Yes Bank' },
  { keywords: ['indusind', 'indus ind'], name: 'IndusInd' },
  { keywords: ['federal bank'], name: 'Federal' },
  { keywords: ['punjab national', 'pnb '], name: 'PNB' },
  { keywords: ['bank of baroda', 'bob '], name: 'BOB' },
  { keywords: ['canara bank'], name: 'Canara' },
  { keywords: ['union bank'], name: 'Union' },
  { keywords: ['rbl bank'], name: 'RBL' },
  { keywords: ['bandhan bank'], name: 'Bandhan' },
  { keywords: ['au small', 'au bank'], name: 'AU Bank' },
  { keywords: ['fi money', 'fi.money'], name: 'Fi Money' },
  { keywords: ['jupiter', 'jupiter bank'], name: 'Jupiter' },
  { keywords: ['niyo', 'niyo bank'], name: 'Niyo' },
];

function detectBank(lines) {
  const text = lines.slice(0, 30).join(' ').toLowerCase(); // check first 30 lines
  for (const bank of BANK_PATTERNS) {
    if (bank.keywords.some(kw => text.includes(kw))) return bank.name;
  }
  return null;
}

// ── Main Export ─────────────────────────────────────────────────
export async function parseBankStatement(file, bankOverride = null) {
  const { lines, rowItems } = await extractTextFromPDF(file);
  const bank = bankOverride || detectBank(lines) || 'Unknown Bank';

  // Detect column layout from header row
  const columnLayout = detectColumnLayout(lines, rowItems);
  const { transactions, lastBalance } = parseTransactions(lines, rowItems, bank, columnLayout);

  // Sort by date descending
  transactions.sort((a, b) => new Date(b.date) - new Date(a.date));

  // Deduplicate by date + amount + type + first 30 chars of description
  const seen = new Set();
  const deduped = transactions.filter(t => {
    const key = `${t.date}_${t.amount}_${t.type}_${t.description.substring(0, 30)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const totalDebit = deduped.filter(t => t.type === 'debit').reduce((s, t) => s + t.amount, 0);
  const totalCredit = deduped.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);

  // Extract closing balance from column parsing or "Closing Balance" line
  let closingBalance = lastBalance;
  if (closingBalance === null) {
    for (let i = lines.length - 1; i >= Math.max(0, lines.length - 20); i--) {
      if (/closing\s+bal/i.test(lines[i])) {
        const cbAmounts = findAmounts(lines[i]);
        if (cbAmounts.length > 0) {
          closingBalance = cbAmounts[cbAmounts.length - 1].value;
          break;
        }
      }
    }
  }

  return {
    transactions: deduped,
    bank,
    closingBalance,
    stats: {
      totalTransactions: deduped.length,
      totalDebit,
      totalCredit,
      net: totalCredit - totalDebit,
    },
    rawLines: lines,
  };
}

export { CATEGORY_RULES, categorize };
